import isArray from 'lodash/isArray'
import omit from 'lodash/omit'
import get from 'lodash/get'
import { useList } from '../useList'
import type { DocumentQueryResult, QueryInput } from '../types/QueryInput'
import { getOperationName } from '@apollo/client/utilities'
import merge from 'lodash/merge'
import { useEffect } from 'react'
import { type DocumentNode, gql } from '@apollo/client'
import { Kind, type DefinitionNode, type FieldNode, type SelectionSetNode } from 'graphql'
import camelCase from 'lodash/camelCase'
import { useBuildDocumentQueryOptions } from './useBuildDocumentQueryOptions'

export type UseDocumentProps<T = any> = {
  _id?: string
  subscribeToUpdates?: boolean
} & QueryInput<T>

export type UseDocumentResult<T = any> = DocumentQueryResult<T>

const extractFieldsFromSelectionSet = (selectionSet: SelectionSetNode, fragmentMap: Record<string, any>): string => {
  if (!selectionSet) return ''

  return selectionSet.selections
    .map((selection: any) => {
      if (selection.kind === Kind.FRAGMENT_SPREAD) {
        const fragmentName = selection.name.value
        const fragment = fragmentMap[fragmentName]

        return fragment ? extractFieldsFromSelectionSet(fragment.selectionSet, fragmentMap) : ''
      }
      if (selection.kind === Kind.FIELD) {
        const subFields = selection.selectionSet ? extractFieldsFromSelectionSet(selection.selectionSet, fragmentMap) : ''
        return subFields ? `${selection.name.value} { ${subFields} }` : selection.name.value
      }
      return ''
    })
    .filter(Boolean)
    .join('\n')
}

const buildSubscriptionQuery = (singleQuery: DocumentNode) => {
  const operationName = getOperationName(singleQuery)

  const fragmentMap: Record<string, any> = {}

  // Store fragments for later lookup
  singleQuery.definitions.forEach((definition: DefinitionNode) => {
    if (definition.kind === Kind.FRAGMENT_DEFINITION) {
      fragmentMap[definition.name.value] = definition
    }
  })

  const operation = singleQuery.definitions.find((def) => def.kind === Kind.OPERATION_DEFINITION)

  if (!operation || operation.kind !== Kind.OPERATION_DEFINITION) {
    throw new Error('Invalid GraphQL query')
  }

  const entityField = operation.selectionSet.selections.find(
    (selection) => selection.kind === Kind.FIELD && selection.name.value === operationName
  ) as FieldNode | undefined

  if (!entityField) {
    throw new Error(`Could not use subscrption from useDocument because we cannot extract fields for entity: ${operationName}`)
  }

  const fields = extractFieldsFromSelectionSet(entityField.selectionSet, fragmentMap)

  //we have default query name as single or all, so we can extract the entity name by removing the prefix
  const entityName = operationName.replace(/^single/, '').replace(/^all/, '')
  const subscriptionName = camelCase(`updated ${entityName}`) //will be converted to "updatedOrder"

  const queryString = `
       subscription ${subscriptionName}($filter: JSON) {
          ${subscriptionName}(filter: $filter) {
              ${fields}  # Inject dynamically extracted fields
          }
      }
  `
  return {
    subscriptionQuery: gql(queryString),
    subscriptionName,
  }
}

export const useDocument = <T = any>({ subscribeToUpdates = false, ...props }: UseDocumentProps<T>): UseDocumentResult<T> => {
  const { entity, limit, skip, filter, options, query } = useBuildDocumentQueryOptions<T>(props)

  const listResult = useList<T>({ query, entity, projection: props.projection, sort: props.sort, limit, skip, filter, options })

  let document: any = get(listResult, 'list', null)
  if (isArray(document)) {
    document = get(listResult, 'list[0]')
  }

  const { error, loading, refetch, subscribeToMore, ...result } = omit(listResult, ['list', 'pageInfo', 'loadMoreEntries'])

  useEffect(() => {
    if (!subscribeToUpdates) return

    const { subscriptionQuery, subscriptionName } = buildSubscriptionQuery(query)

    subscribeToMore({
      document: subscriptionQuery,
      variables: { filter },
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData?.data) return prev

        // we know this is a single document, so it should return as object not array just like from the list
        //even though we query from the the useList result
        const mergeResult = merge(subscriptionData.data[subscriptionName], prev[entity])

        return {
          [entity]: mergeResult,
        }
      },
    })
  }, [subscribeToUpdates, subscribeToMore, entity])

  //return the result, another apollo-related result is included from useList
  //the isEmpty is handled by the list too
  return { error, loading, refetch, ...result, document, subscribeToMore }
}
